import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {AuthService} from "../../services/auth.service";
import {SignupDto} from "../../models/User";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {Subscription} from "rxjs";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {NgClass} from "@angular/common";
import {UserInvitationService} from "src/app/modules/works-companions/services/user-invitation.service";
import {CountrySelectComponent} from "src/app/modules/shared/components/country-select/country-select.component";
import {resolveAuthLocale} from "../../utils/auth-locale.util";
import {SocialLoginButtonsComponent} from "../social-login-buttons/social-login-buttons.component";
import {LegalService} from "../../services/legal.service";
import {LegalAcceptanceControlsComponent} from "../legal-acceptance-controls/legal-acceptance-controls.component";
import type {CurrentLegalVersionsView} from "../../interfaces/current-legal-versions.interface";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import type {UserInvitationPreview} from "src/app/modules/works-companions/interfaces/user-invitation.interface";
import {environment} from "src/environments/environment";

@Component({
  selector: "app-signup-form",
  templateUrl: "./signup-form.component.html",
  styleUrls: ["./signup-form.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    TranslateModule,
    RouterLink,
    CountrySelectComponent,
    SocialLoginButtonsComponent,
    LegalAcceptanceControlsComponent,
    UserAvatarComponent,
  ],
})
export class SignupFormComponent implements OnInit, OnDestroy {
  signupForm: FormGroup;
  error!: CustomError;
  private readonly authLocale = resolveAuthLocale();
  public readonly userLanguage = this.authLocale.language;
  public userCountry: string = this.authLocale.country;
  private readonly subscriptions: Subscription[] = [];
  public showPassword = false;
  public showConfirmPassword = false;
  public submitButtonText?: string;
  public invitationToken?: string;
  public invitationPreview?: UserInvitationPreview;
  public readonly invitationImagesDomain = environment.imagesDomain;
  public readonly failedInvitationCovers = new Set<string>();
  public get invitationGems() {
    return (this.invitationPreview?.recentGems ?? []).filter(
      (gem) => !this.failedInvitationCovers.has(gem.cover)
    );
  }
  public currentLegalVersions?: CurrentLegalVersionsView;
  public versionsUnavailable = true;

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly legalService: LegalService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userInvitationService: UserInvitationService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.signupForm = this.formBuilder.group({
      firstname: [""],
      lastname: [""],
      username: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
      confirm_password: ["", Validators.required],
      acceptTerms: [false, Validators.requiredTrue],
      acceptPrivacy: [false, Validators.requiredTrue],
    });
  }

  ngOnInit() {
    this.translate.defaultLang = this.userLanguage;
    this.translate.reloadLang(this.userLanguage);
    this.translate.use(this.userLanguage);
    this.setSubmitText("SIGN_UP");
    this.loadCurrentLegalVersions();

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("SIGN_UP");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);

    const routeSubscription = this.activatedRoute.queryParamMap.subscribe({
      next: (params) => {
        const token = params.get("invitationToken");
        if (token) {
          this.invitationToken = token;
          this.invitationPreview = undefined;
          this.failedInvitationCovers.clear();
          void this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {invitationToken: null},
            queryParamsHandling: "merge",
            replaceUrl: true,
          });
          const invitationSubscription = this.userInvitationService
            .getInvitation(token)
            .subscribe({
              next: (invitation) => {
                if (this.invitationToken !== token) return;
                if (invitation) {
                  this.invitationPreview = invitation;
                  if (invitation.email)
                    this.signupForm.patchValue({email: invitation.email});
                } else {
                  this.invitationToken = undefined;
                  this.error = {error: {error: "APP_INVITATION_EXPIRED"}};
                }
                this.cdr.markForCheck();
              },
              error: (error: CustomError) => {
                if (this.invitationToken !== token) return;
                this.invitationToken = undefined;
                this.error = error;
                this.cdr.markForCheck();
              },
            });
          this.subscriptions.push(invitationSubscription);
        }
      },
    });
    this.subscriptions.push(routeSubscription);
  }

  public get signupDisabled(): boolean {
    return (
      this.signupForm.invalid || !this.userCountry || this.versionsUnavailable
    );
  }

  public get socialLoginDisabled(): boolean {
    return this.legalAcceptanceMissing || this.versionsUnavailable;
  }

  private get legalAcceptanceMissing(): boolean {
    return (
      this.signupForm.get("acceptTerms")?.invalid === true ||
      this.signupForm.get("acceptPrivacy")?.invalid === true
    );
  }

  private loadCurrentLegalVersions(): void {
    this.versionsUnavailable = true;
    const legalVersionsSubscription = this.legalService
      .getCurrentVersions()
      .subscribe({
        next: (versions) => {
          this.currentLegalVersions = versions;
          this.versionsUnavailable = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = {
            error: {
              error: "LEGAL_VERSIONS_UNAVAILABLE",
            },
          };
          this.versionsUnavailable = true;
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(legalVersionsSubscription);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitButtonText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  onSubmit() {
    const legalVersions = this.getLegalVersionsForSignup();
    if (this.signupForm.invalid || !this.userCountry || !legalVersions) {
      this.error = {
        error: {
          error: legalVersions
            ? "FILL_ALL_FIELDS"
            : "LEGAL_VERSIONS_UNAVAILABLE",
        },
      };
      return;
    }

    if (
      this.signupForm.value.password !== this.signupForm.value.confirm_password
    ) {
      this.error = {
        error: {
          error: "PASSWORD_NO_MATCH",
        },
      };
      return;
    }

    this.setSubmitText("CREATING_ACCOUNT");

    const user: SignupDto = {
      workResumeUrl:
        this.activatedRoute.snapshot.queryParamMap.get("redirectTo") ??
        undefined,
      language: this.userLanguage,
      country: this.userCountry,
      email: this.signupForm.value.email,
      firstname: this.signupForm.value.firstname,
      lastname: this.signupForm.value.lastname,
      username: this.signupForm.value.username,
      password: this.signupForm.value.password,
      confirm_password: this.signupForm.value.confirm_password,
      invitationToken: this.invitationToken,
      termsVersion: legalVersions.termsVersion,
      privacyVersion: legalVersions.privacyVersion,
    };

    const signUpSubscription = this.authService.signup(user).subscribe({
      next: () => {
        this.router.navigate(["/account-created"]);
      },
      error: (error: CustomError) => {
        this.error = error;
        this.setSubmitText("SIGN_UP");
        this.cdr.markForCheck();
      },
    });
    this.subscriptions.push(signUpSubscription);
  }

  private getLegalVersionsForSignup(): {
    termsVersion: string;
    privacyVersion: string;
  } | null {
    if (!this.currentLegalVersions || this.versionsUnavailable) {
      return null;
    }

    return {
      termsVersion: this.currentLegalVersions.terms.version,
      privacyVersion: this.currentLegalVersions.privacy.version,
    };
  }

  onKeyUp(event: KeyboardEvent, type: string): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "showPassword") {
        this.showPassword = !this.showPassword;
      } else if (type === "showConfirmPassword") {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
